import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { invokeChannel } from '~/utils/constants';
import { isWeb } from '~/utils/helper/device';

type ConnectivityState = {
  isOffline: boolean;
  offlineDisplayName: string;
};

type OfflineModeContextValue = ConnectivityState & {
  isReady: boolean;
  isRetrying: boolean;
  retryConnection: () => Promise<void>;
};

const isBundledOfflinePage = !isWeb && window.__NANIBOX_OFFLINE__ === true;

const OfflineModeContext = createContext<OfflineModeContextValue>({
  isOffline: false,
  offlineDisplayName: '',
  isReady: true,
  isRetrying: false,
  retryConnection: () => Promise.resolve(),
});

export const OfflineModeProvider = ({ children }: { children: ReactNode }) => {
  const [connectivity, setConnectivity] = useState<ConnectivityState>({
    isOffline: isBundledOfflinePage,
    offlineDisplayName: '',
  });
  const [isReady, setIsReady] = useState(!isBundledOfflinePage);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (!isBundledOfflinePage) return;

    void Promise.resolve(window.api.invoke(invokeChannel.getConnectivityState))
      .then(state =>
        setConnectivity({
          ...(state as ConnectivityState),
          // 本機備援頁不可因主程序狀態同步期間短暫掛載線上功能。
          isOffline: true,
        }),
      )
      .finally(() => setIsReady(true));
  }, []);

  const retryConnection = useCallback(async () => {
    if (!isBundledOfflinePage || isRetrying) return;

    setIsRetrying(true);
    try {
      const state = (await Promise.resolve(
        window.api.invoke(invokeChannel.retryConnection),
      )) as ConnectivityState & { isOnline: boolean };
      if (!state.isOnline) {
        setConnectivity({
          isOffline: true,
          offlineDisplayName: state.offlineDisplayName,
        });
      }
    } finally {
      setIsRetrying(false);
    }
  }, [isRetrying]);

  return (
    <OfflineModeContext.Provider
      value={{
        ...connectivity,
        isReady,
        isRetrying,
        retryConnection,
      }}
    >
      {children}
    </OfflineModeContext.Provider>
  );
};

export const useOfflineMode = () => useContext(OfflineModeContext);
